import { Link } from 'react-router-dom';
import { Callout, Card, CardBody } from '../components/ui.jsx';
import BrandMark from '../components/BrandMark.jsx';
import { Lock, Phone, ShieldCheck } from 'lucide-react';

const PHONE = '1-855-232-7996';
const PHONE_HREF = 'tel:+18552327996';
const SUPPORT = 'support@3lumpers.com';
const UPDATED = '13 October 2026';

/**
 * The privacy policy, in the same voice as the rest of the site.
 *
 * It has to match what the software actually does, because it is the page the
 * App Store links to and the one a lumper reads when he wonders who can see his
 * licence. Written from the code: what is stored on our own server, what is
 * encrypted, what never comes back out.
 */
const LUMPER_ITEMS = [
  [
    'Who you are',
    'Your name, email, phone, and the city, state and ZIP you work from — so we know which docks are near you.',
  ],
  [
    'Your paperwork',
    'A W-9 with your taxpayer number, photos of the front and back of your government ID, and the signature you draw on your phone. The taxpayer number is kept encrypted and the app only ever shows the last four digits back to you.',
  ],
  [
    'Your work',
    'The jobs you took, when you checked in and finished, the pallet counts you logged, and the photos you send from a dock as proof of what was unloaded.',
  ],
  [
    'Where you are',
    'The app uses your place to put you on the map of work near you, so you can see which docks are close. When you say "I am here", the office also records the place that tap came from, so a check-in at the wrong dock is visible. If your phone will not give a location, the map still lists the jobs and the tap still works.',
  ],
  [
    'Your pay',
    'How you want to be paid, what you earned, and the ratings customers left about the work.',
  ],
  [
    'Your crew',
    'The names and phone numbers of the men you add to your crew. Most of them never install the app, and we keep only what you typed.',
  ],
];

const CLIENT_ITEMS = [
  [
    'Your account',
    'The business name, the people we talk to, and the addresses of your docks.',
  ],
  [
    'Your jobs and bills',
    'What you posted, who worked it, the photos and sign-offs from the crew, your invoices and what has been paid.',
  ],
  [
    'Card payments',
    'If you pay by card, the payment is handled by our payment processor. The card number never reaches our server — we keep only the reference they give us and the last four digits.',
  ],
];

export default function Privacy() {
  return (
    <div>
      <div className="row gap-12 between" style={{ padding: '16px 18px' }}>
        <Link to="/" className="row gap-8" style={{ textDecoration: 'none' }}>
          <BrandMark size={26} />
          <span style={{ fontWeight: 650 }}>3Lumpers</span>
        </Link>
        <a href={PHONE_HREF} className="row gap-6">
          <Phone size={13} /> {PHONE}
        </a>
      </div>

      <div style={{ padding: '34px 18px 64px', maxWidth: 680, margin: '0 auto' }}>
        <span className="label">Privacy</span>
        <h1 className="mt-8">What we keep, and why</h1>
        <p className="lede mt-12">
          This is the plain version, and it matches what the software actually does. If anything
          here is not clear, call the office and somebody will explain it.
        </p>
        <p className="dim mt-8" style={{ fontSize: 'var(--fs-sm)' }}>
          Last updated {UPDATED}. 3Lumpers Services LLC, Medley, Florida.
        </p>

        <Card className="mt-28">
          <CardBody>
            <h3>If you work for us</h3>
            <div className="col gap-16 mt-16">
              {LUMPER_ITEMS.map(([title, body]) => (
                <div key={title}>
                  <div style={{ fontWeight: 650 }}>{title}</div>
                  <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                    {body}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        <Card className="mt-20">
          <CardBody>
            <h3>If we work for you</h3>
            <div className="col gap-16 mt-16">
              {CLIENT_ITEMS.map(([title, body]) => (
                <div key={title}>
                  <div style={{ fontWeight: 650 }}>{title}</div>
                  <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                    {body}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        <Card className="mt-20">
          <CardBody>
            <h3>Why we keep it</h3>
            <p className="muted mt-8" style={{ fontSize: 'var(--fs-sm)' }}>
              To put the right crew at the right dock, to pay people the same day they work, to bill
              the customer whose freight moved, and because the law requires a W-9 and a government
              ID on file before an independent contractor is paid. That is the whole list. We do not
              sell anything to anybody, and nothing here is used to advertise to you.
            </p>
          </CardBody>
        </Card>

        <Card className="mt-20">
          <CardBody>
            <h3>Who can see it</h3>
            <p className="muted mt-8" style={{ fontSize: 'var(--fs-sm)' }}>
              The 3Lumpers office. The customer whose dock you worked at sees your first name, your
              rating, and what came off the trailer — never your paperwork. The payment processor
              sees what is needed to move money. The tax authorities see the W-9 when they ask.
              Nobody else, ever.
            </p>
          </CardBody>
        </Card>

        <Card className="mt-20">
          <CardBody>
            <h3>Where it lives, and for how long</h3>
            <p className="muted mt-8" style={{ fontSize: 'var(--fs-sm)' }}>
              On our own server in the United States — the database and the photos, not a
              third-party cloud. We keep a worker's record while he works with us and for seven
              years afterwards, because that is how long the tax records have to be kept. Photos
              from a job are kept as the proof of what was unloaded and what was signed off.
            </p>
          </CardBody>
        </Card>

        <Card className="mt-20">
          <CardBody>
            <h3>Your choices</h3>
            <p className="muted mt-8" style={{ fontSize: 'var(--fs-sm)' }}>
              Email <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> or call <a href={PHONE_HREF}>{PHONE}</a> and we
              will send you a copy of what we hold about you, fix anything wrong, or delete what we
              are not required to keep. Deleting the app from your phone does not delete your
              record — the office has to do that, and will when you ask.
            </p>
          </CardBody>
        </Card>

        <Callout tone="blue" icon={ShieldCheck} title="What the app on your phone asks for" className="mt-20">
          The camera and your photos, only when you take a picture. The place you are, to show you
          the work near you on the map and to record where a check-in came from. Notifications, only
          if you say yes. It does not read your contacts, your messages, or anything else on the
          phone, and there is no advertising or tracking code in it.
        </Callout>

        <Card className="mt-20">
          <CardBody>
            <div className="row gap-10">
              <Lock size={16} className="amber" />
              <h3>Two more things</h3>
            </div>
            <p className="muted mt-8" style={{ fontSize: 'var(--fs-sm)' }}>
              This is a work app for adults; we do not knowingly keep anything about anybody under
              18. And when this page changes, the date at the top changes with it — if it is a real
              change to what we collect, the app tells workers.
            </p>
          </CardBody>
        </Card>

        <p className="muted mt-20">
          Questions about any of this? Call <a href={PHONE_HREF}>{PHONE}</a>, 6am to 8pm daily, or
          email <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>. Looking for work?{' '}
          <Link to="/signup?path=lumper" className="amber">
            Sign up
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
